import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { soundManager } from '../../utils/sounds';

type CoachCategory = 'gameplay' | 'scoring' | 'shop' | 'combat' | 'system';

interface TutorialCoachProps {
  tipId: string;
  title: string;
  body: React.ReactNode;
  category: CoachCategory;
  position?: 'center' | 'top' | 'bottom';
  show: boolean;
  onDismiss: () => void;
  onSkipAll: () => void;
  stepLabel?: string;

  // Interactive teacher props
  highlightSelector?: string;
  requiredAction?: string;
  actionCompleted?: boolean;
  dismissLabel?: string;
}

const categoryColors: Record<CoachCategory, { border: string; glow: string; badge: string }> = {
  gameplay: {
    border: 'border-arcade-cyan/60',
    glow: 'shadow-[0_0_20px_rgba(0,255,255,0.15)]',
    badge: 'bg-arcade-cyan/20 text-arcade-cyan',
  },
  scoring: {
    border: 'border-arcade-pink/60',
    glow: 'shadow-[0_0_20px_rgba(255,0,128,0.15)]',
    badge: 'bg-arcade-pink/20 text-arcade-pink',
  },
  shop: {
    border: 'border-emerald-400/60',
    glow: 'shadow-[0_0_20px_rgba(0,255,150,0.15)]',
    badge: 'bg-emerald-500/20 text-emerald-400',
  },
  combat: {
    border: 'border-amber-400/60',
    glow: 'shadow-[0_0_20px_rgba(255,180,0,0.15)]',
    badge: 'bg-amber-500/20 text-amber-400',
  },
  system: {
    border: 'border-purple-400/60',
    glow: 'shadow-[0_0_20px_rgba(168,85,247,0.15)]',
    badge: 'bg-purple-500/20 text-purple-400',
  },
};

const positionClass: Record<string, string> = {
  center: 'items-center',
  top: 'items-start pt-24',
  bottom: 'items-end pb-24',
};

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

export const TutorialCoach: React.FC<TutorialCoachProps> = ({
  title,
  body,
  category,
  position = 'center',
  show,
  onDismiss,
  onSkipAll,
  stepLabel,
  highlightSelector,
  requiredAction,
  actionCompleted,
  dismissLabel,
}) => {
  const colors = categoryColors[category];
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);
  const celebrationTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const prevActionCompletedRef = useRef(actionCompleted);

  // Measure spotlight target
  useEffect(() => {
    if (!show || !highlightSelector) {
      setSpotlight(null);
      return;
    }

    const measure = () => {
      const el = document.querySelector(highlightSelector);
      if (el) {
        const rect = el.getBoundingClientRect();
        const pad = 6;
        setSpotlight({
          top: rect.top - pad,
          left: rect.left - pad,
          width: rect.width + pad * 2,
          height: rect.height + pad * 2,
        });
      } else {
        setSpotlight(null);
      }
    };

    measure();
    // Re-measure on resize/scroll
    const interval = setInterval(measure, 500);
    window.addEventListener('resize', measure);
    return () => {
      clearInterval(interval);
      window.removeEventListener('resize', measure);
    };
  }, [show, highlightSelector]);

  // Celebration on action completion
  useEffect(() => {
    if (actionCompleted && !prevActionCompletedRef.current) {
      soundManager.play('powerup');
      setShowCelebration(true);
      celebrationTimerRef.current = setTimeout(() => {
        setShowCelebration(false);
        onDismiss();
      }, 800);
    }
    prevActionCompletedRef.current = actionCompleted;

    return () => {
      if (celebrationTimerRef.current) clearTimeout(celebrationTimerRef.current);
    };
  }, [actionCompleted, onDismiss]);

  const isInteractive = !!requiredAction;
  const showDismissButton = !isInteractive || actionCompleted;

  // Determine bubble position based on spotlight
  const getBubblePosition = useCallback((): string => {
    if (!spotlight) return positionClass[position];
    // Place bubble below if spotlight is in top half, above if bottom half
    const spotMid = spotlight.top + spotlight.height / 2;
    const screenMid = window.innerHeight / 2;
    return spotMid < screenMid ? 'items-end pb-8' : 'items-start pt-20';
  }, [spotlight, position]);

  return (
    <AnimatePresence>
      {show && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.15 }}
          className="fixed inset-0 z-[65] pointer-events-none"
        >
          {/* Spotlight overlay: 4 dark rectangles around the cutout */}
          {spotlight ? (
            <>
              {/* Top bar */}
              <div
                className="absolute bg-black/50 pointer-events-auto"
                style={{ top: 0, left: 0, right: 0, height: spotlight.top }}
                onClick={!isInteractive ? onDismiss : undefined}
              />
              {/* Bottom bar */}
              <div
                className="absolute bg-black/50 pointer-events-auto"
                style={{
                  top: spotlight.top + spotlight.height,
                  left: 0,
                  right: 0,
                  bottom: 0,
                }}
                onClick={!isInteractive ? onDismiss : undefined}
              />
              {/* Left bar */}
              <div
                className="absolute bg-black/50 pointer-events-auto"
                style={{
                  top: spotlight.top,
                  left: 0,
                  width: spotlight.left,
                  height: spotlight.height,
                }}
                onClick={!isInteractive ? onDismiss : undefined}
              />
              {/* Right bar */}
              <div
                className="absolute bg-black/50 pointer-events-auto"
                style={{
                  top: spotlight.top,
                  left: spotlight.left + spotlight.width,
                  right: 0,
                  height: spotlight.height,
                }}
                onClick={!isInteractive ? onDismiss : undefined}
              />
              {/* Pulsing border ring around cutout */}
              <motion.div
                className="absolute rounded-lg border-2 border-arcade-cyan/80 pointer-events-none"
                style={{
                  top: spotlight.top,
                  left: spotlight.left,
                  width: spotlight.width,
                  height: spotlight.height,
                }}
                animate={{
                  boxShadow: [
                    '0 0 8px rgba(0,255,255,0.3), inset 0 0 8px rgba(0,255,255,0.1)',
                    '0 0 20px rgba(0,255,255,0.6), inset 0 0 20px rgba(0,255,255,0.2)',
                    '0 0 8px rgba(0,255,255,0.3), inset 0 0 8px rgba(0,255,255,0.1)',
                  ],
                }}
                transition={{ duration: 1.5, repeat: Infinity }}
              />
            </>
          ) : (
            /* No spotlight — full dim backdrop */
            <div
              className="absolute inset-0 bg-black/40 pointer-events-auto"
              onClick={!isInteractive ? onDismiss : undefined}
            />
          )}

          {/* Coach bubble */}
          <div
            className={`absolute inset-0 flex justify-center px-4 pointer-events-none ${getBubblePosition()}`}
          >
            <motion.div
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.97 }}
              transition={{ type: 'spring', stiffness: 400, damping: 30 }}
              className={`relative arcade-panel rounded-xl border-2 ${colors.border} ${colors.glow} max-w-[480px] w-full p-5 pointer-events-auto`}
            >
              {/* Celebration flash */}
              <AnimatePresence>
                {showCelebration && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 1.2 }}
                    className="absolute inset-0 flex items-center justify-center bg-black/60 rounded-xl z-10"
                  >
                    <span className="font-display text-3xl font-bold text-arcade-cyan drop-shadow-[0_0_20px_rgba(0,255,255,0.8)]">
                      Great!
                    </span>
                  </motion.div>
                )}
              </AnimatePresence>

              {/* Header row */}
              <div className="flex items-start justify-between gap-3 mb-3">
                <div className="flex items-center gap-2">
                  <span
                    className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-0.5 rounded ${colors.badge}`}
                  >
                    {category}
                  </span>
                  {stepLabel && <span className="text-[10px] text-white/40">{stepLabel}</span>}
                </div>
              </div>

              {/* Title */}
              <h3 className="font-display text-lg font-bold text-white mb-2">{title}</h3>

              {/* Body */}
              <div className="text-sm text-white/80 leading-relaxed mb-4">{body}</div>

              {/* Required action instruction */}
              {requiredAction && !actionCompleted && (
                <div className="mb-4 rounded-lg border border-arcade-cyan/30 bg-arcade-cyan/5 px-3 py-2">
                  <div className="flex items-center gap-2">
                    <motion.span
                      animate={{ opacity: [0.5, 1, 0.5] }}
                      transition={{ duration: 1.5, repeat: Infinity }}
                      className="text-arcade-cyan text-xs"
                    >
                      {'>>'}
                    </motion.span>
                    <span className="text-sm font-semibold text-arcade-cyan">{requiredAction}</span>
                  </div>
                </div>
              )}

              {/* Actions */}
              <div className="flex items-center justify-between">
                <button
                  type="button"
                  onClick={onSkipAll}
                  className="text-xs text-white/30 hover:text-white/50 transition-colors"
                >
                  Skip all tips
                </button>
                {showDismissButton && !showCelebration && (
                  <button
                    type="button"
                    onClick={onDismiss}
                    className="arcade-btn pixel-corners px-5 py-1.5 text-sm bg-gradient-to-r from-purple-600 to-pink-600"
                  >
                    {dismissLabel ?? 'Got it!'}
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
